import { Download, FileText, Mail, Music2, Radio, Disc3, HeartHandshake, ScrollText } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SocialIcons } from "@/components/SocialIcons";

const Section = ({ icon: Icon, title, children, testid }) => (
  <Reveal>
    <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-8 sm:p-10 h-full" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-10 rounded-full flex items-center justify-center border border-gold-400/40 text-gold-300">
          <Icon size={17} />
        </span>
        <h2 className="font-serif text-xl sm:text-2xl text-slate-50">{title}</h2>
      </div>
      <div className="text-sm text-slate-300/90 font-light leading-relaxed space-y-4">{children}</div>
    </div>
  </Reveal>
);

export default function Press() {
  return (
    <div className="pt-28 pb-24" data-testid="press-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="eyebrow mb-3">Press &amp; Media</p>
          <h1 className="font-serif text-3xl sm:text-5xl text-slate-50 leading-tight">
            Open Horizon Records · <span className="text-gold-gradient">Music Beyond The Horizon</span>
          </h1>
          <h2 className="font-serif text-xl sm:text-2xl text-gold-200 mt-8">Welcome to our press room</h2>
          <p className="mt-4 max-w-2xl mx-auto text-slate-300/85 font-light leading-relaxed">
            Discover the music, stories and creative vision behind Open Horizon Records. Whether you're a
            radio presenter, playlist curator, journalist or music blogger, this page brings together the
            information and media assets you need.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <a href="/assets/Open-Horizon-Press-Kit.zip" download className="btn-gold" data-testid="press-kit-download">
              <Download size={16} /> Download Complete Press Kit (ZIP)
            </a>
            <a href="/assets/Open-Horizon-Press-Bio.pdf" download className="btn-ghost-gold" data-testid="press-bio-download">
              <FileText size={16} /> Download Press Biography (PDF)
            </a>
          </div>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8">
          <Section icon={ScrollText} title="About Open Horizon Records" testid="press-about">
            <p>
              Open Horizon Records is an independent music label with a passion for original songs,
              memorable melodies and music that connects with people. From uplifting anthems and
              feel-good moments to reflective ballads and personal stories, our music explores the
              emotions and experiences that make life what it is.
            </p>
            <p className="font-serif text-gold-200 text-base">
              Every song has a story. Every story has a soundtrack.
            </p>
          </Section>

          <Section icon={Disc3} title="Music & releases" testid="press-releases">
            <p>
              Explore our growing catalogue, including{" "}
              <span className="text-gold-200">Every Season Leaves a Mark</span>,{" "}
              <span className="text-gold-200">Chasing Joy</span>,{" "}
              <span className="text-gold-200">Let the Leaves Fall</span> and{" "}
              <span className="text-gold-200">Letting Go</span>. For verified release dates, streaming
              links and radio-ready audio, please contact us directly.
            </p>
            <p>
              <a href="/music" className="text-gold-300 hover:text-gold-200 transition-colors underline underline-offset-4" data-testid="press-catalogue-link">
                Browse the full catalogue
              </a>
            </p>
          </Section>

          <Section icon={Radio} title="For radio & playlist curators" testid="press-radio">
            <p>
              Interested in featuring our music? Get in touch for airplay-quality audio, release notes,
              credits, interview enquiries or playlist submissions. Tell us your station, programme or
              playlist and which track interests you.
            </p>
            <p>
              <a href="mailto:openhorizonrecords@outlook.com" className="btn-gold !px-5 !py-2.5 text-xs" data-testid="press-contact-btn">
                <Mail size={14} /> Contact the Label
              </a>
            </p>
          </Section>

          <Section icon={HeartHandshake} title="Bespoke music" testid="press-bespoke">
            <p>
              We also turn personal stories into original, professionally produced songs for weddings,
              anniversaries, celebrations, tributes and memorials.
            </p>
            <p className="font-serif text-gold-200 text-base">Your Story. Your Song. Created With You.</p>
          </Section>
        </div>

        <Reveal className="mt-8">
          <div className="rounded-2xl border border-gold-400/25 bg-navy-900/70 p-8 sm:p-10" data-testid="press-usage">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-full flex items-center justify-center border border-gold-400/40 text-gold-300">
                <Music2 size={17} />
              </span>
              <h2 className="font-serif text-xl sm:text-2xl text-slate-50">Media usage &amp; enquiries</h2>
            </div>
            <div className="text-sm text-slate-300/90 font-light leading-relaxed space-y-4 max-w-3xl">
              <p>
                Press materials may be used for relevant editorial, review, radio and playlist coverage
                with credit to Open Horizon Records. Please preserve original artwork and request
                permission for any other use.
              </p>
              <p>
                Email:{" "}
                <a href="mailto:openhorizonrecords@outlook.com" className="text-gold-300 hover:text-gold-200 transition-colors" data-testid="press-email-link">
                  openhorizonrecords@outlook.com
                </a>
              </p>
              <p className="text-xs text-slate-500">
                Release dates, credits and streaming availability should be confirmed before publication.
                The downloadable kit contains original logo assets and selected release artwork — no audio
                masters or unpublished release details are included, and the kit does not grant
                broadcasting or reproduction rights.
              </p>
            </div>
            <div className="mt-6">
              <SocialIcons size={18} />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
